/**
 * Selectable fonts for the card's English/Latin text. Indic-script text
 * (name, address, labels) is unaffected - that always uses the correct
 * Noto Sans stack for the selected state's script (see .ac-script-* in
 * AadhaarCard.css), regardless of this choice.
 *
 * Arial is the default because it is the single most universally-available
 * system font across Windows/macOS/Linux, so the common case needs no web
 * font at all. "Times / Tinos" is kept as an option for anyone who wants
 * the closer match to a real e-Aadhaar's actual embedded font (confirmed
 * by inspecting one directly - see AadhaarCard.css).
 *
 * Everything here is either a system font or one already loaded by
 * public/index.html. That is deliberate: exports rasterise through
 * html2canvas, which draws with whatever the browser has resolved, so a font
 * that has not actually loaded would silently fall back and the download
 * would not match the preview. Adding a new web font means adding it to the
 * stylesheet link in index.html as well, not just to this list.
 *
 * `group` only drives the optgroup headings in the picker.
 */
export const CARD_FONTS = [
  // --- Sans ---------------------------------------------------------
  {
    key: "arial",
    label: "Arial (default)",
    group: "Sans",
    stack: 'Arial, "Liberation Sans", Helvetica, sans-serif',
  },
  {
    key: "helvetica",
    label: "Helvetica",
    group: "Sans",
    stack: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  {
    key: "verdana",
    label: "Verdana",
    group: "Sans",
    stack: "Verdana, Geneva, DejaVu Sans, sans-serif",
  },
  {
    key: "tahoma",
    label: "Tahoma",
    group: "Sans",
    stack: 'Tahoma, Verdana, "DejaVu Sans", sans-serif',
  },
  {
    key: "trebuchet",
    label: "Trebuchet MS",
    group: "Sans",
    stack: '"Trebuchet MS", "Lucida Grande", Tahoma, sans-serif',
  },
  {
    key: "segoe",
    label: "Segoe UI",
    group: "Sans",
    stack: '"Segoe UI", Frutiger, "Helvetica Neue", Arial, sans-serif',
  },
  {
    key: "calibri",
    label: "Calibri",
    group: "Sans",
    stack: 'Calibri, Candara, "Segoe UI", sans-serif',
  },
  {
    key: "roboto",
    label: "Roboto",
    group: "Sans",
    stack: 'Roboto, "Droid Sans", "Helvetica Neue", Arial, sans-serif',
  },
  {
    key: "inter",
    label: "Inter",
    group: "Sans",
    stack: 'Inter, "Segoe UI", Roboto, sans-serif',
  },
  {
    key: "century-gothic",
    label: "Century Gothic",
    group: "Sans",
    stack: '"Century Gothic", "URW Gothic", "Avant Garde", sans-serif',
  },
  {
    key: "franklin",
    label: "Franklin Gothic",
    group: "Sans",
    stack: '"Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif',
  },

  // --- Serif --------------------------------------------------------
  {
    key: "tinos",
    label: "Times / Tinos",
    group: "Serif",
    stack: 'Tinos, "Times New Roman", Times, "Liberation Serif", Georgia, serif',
  },
  {
    key: "georgia",
    label: "Georgia",
    group: "Serif",
    stack: 'Georgia, "Times New Roman", serif',
  },
  {
    key: "cambria",
    label: "Cambria",
    group: "Serif",
    stack: 'Cambria, Georgia, "Times New Roman", serif',
  },
  {
    key: "palatino",
    label: "Palatino",
    group: "Serif",
    stack: 'Palatino, "Palatino Linotype", "Book Antiqua", Georgia, serif',
  },
  {
    key: "garamond",
    label: "Garamond",
    group: "Serif",
    stack: 'Garamond, "EB Garamond", "Times New Roman", serif',
  },
  {
    key: "bookman",
    label: "Bookman",
    group: "Serif",
    stack: '"Bookman Old Style", "URW Bookman L", Georgia, serif',
  },

  // --- Mono ---------------------------------------------------------
  {
    key: "courier",
    label: "Courier New",
    group: "Mono",
    stack: '"Courier New", Courier, "Liberation Mono", monospace',
  },
  {
    key: "jetbrains",
    label: "JetBrains Mono",
    group: "Mono",
    stack: '"JetBrains Mono", ui-monospace, Consolas, monospace',
  },
  {
    key: "consolas",
    label: "Consolas",
    group: "Mono",
    stack: 'Consolas, Menlo, "DejaVu Sans Mono", monospace',
  },
];

/** Picker headings, in the order they should appear. */
export const CARD_FONT_GROUPS = ["Sans", "Serif", "Mono"];

export const DEFAULT_CARD_FONT = "arial";

export function getCardFont(key) {
  return CARD_FONTS.find((f) => f.key === key) || CARD_FONTS[0];
}
